import { useMutation } from "@tanstack/react-query"
import { Paperclip } from "lucide-react"
import { useState } from "react"

import { ErrorApi, pedir } from "@/api/cliente"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"

/**
 * Los soportes del encargo: los que llegaron al abrirlo y los que se añaden después.
 *
 * ⚠ **Estos no son los adjuntos de un comentario.** Cuelgan de la tarea y no de una
 * frase: la factura, el BL, la captura del error. El servidor los distingue por
 * `comentario_id IS NULL` y los devuelve aparte, y si aquí no se pintaran serían
 * archivos subidos que nadie puede volver a ver.
 *
 * ⚠ **Se elige, se previsualiza y entonces se sube.** Subir al soltar el archivo
 * parece más rápido hasta que se sube el que no era: con la miniatura delante, el
 * error se ve antes de gastar la petición.
 */

export type Adjunto = {
  id: number
  nombre_original: string
  mime: string
  bytes: number
}

type Props = {
  tareaId: number
  adjuntos: Adjunto[]
  puedeAdjuntar: boolean
  onSubido: () => void
}

export function SoportesTarea({ tareaId, adjuntos, puedeAdjuntar, onSubido }: Props) {
  const [pendientes, setPendientes] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])

  const recibir = (archivos: File[]) => {
    const siguiente = sumar(pendientes, archivos)
    setPendientes(siguiente.adjuntos)
    setRechazados(siguiente.rechazados)
  }

  const subir = useMutation({
    mutationFn: async () => {
      for (const pendiente of pendientes) {
        const formulario = new FormData()
        formulario.append("archivo", pendiente.archivo)
        await pedir(`/api/v1/tareas/${tareaId}/adjuntos`, { method: "POST", body: formulario })
      }
    },
    onSuccess: () => {
      setPendientes([])
      onSubido()
    },
  })

  return (
    // El pegado se escucha en la sección entera: sube por el árbol desde donde
    // esté el cursor, que es como llega de verdad una captura.
    <section
      className="mt-5"
      onPaste={(e) => {
        const archivos = archivosPegados(e.clipboardData, pendientes.length)
        if (archivos.length === 0) return
        e.preventDefault()
        recibir(archivos)
      }}
    >
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <Paperclip size={14} aria-hidden={true} />
        Soportes
        <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {adjuntos.length}
        </span>
      </h3>

      {adjuntos.length === 0 ? (
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no hay soportes.
        </p>
      ) : (
        <ul className="mt-1 space-y-1">
          {adjuntos.map((a) => (
            <li key={a.id}>
              {/* Endpoint autenticado, no `storage/`, que no se sirve como estático.
                  `Content-Disposition` decide en el servidor si se abre o se
                  descarga: PDF e imagen en línea, lo demás como archivo. */}
              <a
                href={`/api/v1/tareas/adjuntos/${a.id}/contenido`}
                target="_blank"
                rel="noreferrer"
                data-testid={`soporte-${a.id}`}
                className="flex items-center gap-1 text-xs underline"
                style={{ color: "var(--fg-muted)" }}
              >
                <Paperclip size={12} aria-hidden={true} />
                {a.nombre_original}
              </a>
            </li>
          ))}
        </ul>
      )}

      {puedeAdjuntar && (
        <>
          <AdjuntosNuevos
            adjuntos={pendientes}
            rechazados={rechazados}
            onArchivos={recibir}
            onQuitar={(id) => setPendientes(pendientes.filter((p) => p.id !== id))}
          />
          {pendientes.length > 0 && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                disabled={subir.isPending}
                onClick={() => subir.mutate()}
                className="boton-primario px-3 py-1.5 text-sm disabled:opacity-50"
              >
                {subir.isPending ? "Subiendo…" : `Subir ${pendientes.length}`}
              </button>
            </div>
          )}
          {subir.isError && (
            <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
              {subir.error instanceof ErrorApi
                ? subir.error.message
                : "No se pudo subir el soporte."}
            </p>
          )}
        </>
      )}
    </section>
  )
}
